import React, { useState } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';  // Importing ClipLoader from react-spinners

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const api=process.env.REACT_APP_API_URL;

const CheckoutForm = ({data, jobDescriptionFile, jobDescriptionText, resumeFile }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const token = Cookies.get('access_token');
    if (!stripe || !elements) {
      return;
    }
    const session = localStorage.getItem('sessionId');

    if (!session) {
      toast.error('No active resume session. Please re-upload your resume.');
      return;
    }

    setLoading(true); // Start loading state

    let response;
    try {
      response = await fetch(`${api}create-payment-intent/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Session-Id': session,
        },
        body: JSON.stringify({ session_id: session }),
        credentials: 'include',
      });
    } catch (err) {
      toast.error('Network error reaching payment service. Please try again.');
      setLoading(false);
      return;
    }

    if (!response.ok) {
      let detail = `Payment setup failed (HTTP ${response.status})`;
      try {
        const errBody = await response.json();
        detail = errBody.message || errBody.error || detail;
      } catch (_) {}
      toast.error(detail);
      setErrorMessage(detail);
      setLoading(false);
      return;
    }

    const { clientSecret, id } = await response.json();
    if (!clientSecret) {
      toast.error('Payment service did not return a client secret. Please try again.');
      setLoading(false);
      return;
    }
    localStorage.setItem('intent_id', id);

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: elements.getElement(CardElement) },
    });

    if (error) {
      setErrorMessage(error.message);
      toast.error(error.message);
      setLoading(false); // Stop loading state
    } else if (paymentIntent.status === 'succeeded') {
      setPaymentSuccess(true);

      const accessToken = Cookies.get("access_token");
      fetch(`${api}payment-status/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'X-Session-Id': session,
        },
        body: JSON.stringify({
          intent_id: localStorage.getItem('intent_id'),
          session_id: session,
        }),
        credentials: 'include',
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to update payment status');
          }
          return response.json();
        })
        .then((data1) => {
          toast.success('Payment status updated successfully!');

          const completedSteps = JSON.parse(localStorage.getItem('completedSteps')) || [];
          if (!completedSteps.includes('pay')) {
            completedSteps.push('pay');
            localStorage.setItem('completedSteps', JSON.stringify(completedSteps));
          }

          setTimeout(() => {
            setLoading(false); 
            navigate('/fullScore', {
              state: { data, jobDescriptionFile, jobDescriptionText, resumeFile },
            });
          }, 1500);
        })
        .catch((error) => {
          toast.error('Failed to update payment status. Please try again.');
          setLoading(false); // Stop loading state
        });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <CardElement className="mt-8" />
        <div className="w-full flex justify-center items-center">
          <button
            className="btn bg-black text-white hover:bg-red-600 hover:text-orange font-bold py-2 px-4 rounded mt-8 transition duration-300 ease-in-out"
            type="submit"
            disabled={!stripe || loading} // Disable if loading
          >
            Pay Here
          </button>
        </div>

        {errorMessage && <div className="text-red-500 mt-2">{errorMessage}</div>}
        {paymentSuccess && <div className="text-green-500 mt-2">Payment Successful!</div>}

        {/* Show loader when loading state is true */}

        <div className='w-full flex justify-center items-center'>
        {loading && (
          <div className="loader mt-4">
            <ClipLoader color="#2c5141" loading={loading} size={50} />
          </div>
        )}
        </div>
      </form>
    </>
  );
};

const Payment = ({ setPaymentSuccess, data, jobDescriptionFile, jobDescriptionText, resumeFile }) => (
  <Elements stripe={stripePromise}>
    <CheckoutForm 
      setPaymentSuccess={setPaymentSuccess} 
      data={data} 
      jobDescriptionFile={jobDescriptionFile} 
      jobDescriptionText={jobDescriptionText} 
      resumeFile={resumeFile} 
    />
  </Elements>
);

export default Payment;
